/**
 * MobileEditorPane — CodeMirror 6 based mobile code editor
 *
 * Why CodeMirror instead of Monaco on mobile:
 *  - Uses native contenteditable → proper touch cursor handles
 *  - Works seamlessly with Android/iOS virtual keyboards
 *  - No hidden-textarea issues, no spurious spaces
 *  - Lightweight and fast to load
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView, keymap } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";
import { undo, redo, indentMore, indentLess } from "@codemirror/commands";
import {
  search, searchKeymap, openSearchPanel, closeSearchPanel,
} from "@codemirror/search";
import { langs } from "@uiw/codemirror-extensions-langs";
import {
  Undo2, Redo2, Copy, ClipboardPaste,
  ZoomIn, ZoomOut, AlignJustify, AlignLeft, Search,
} from "lucide-react";
import { getTheme } from "../lib/themes";

// ── Language support map (Monaco lang id → CodeMirror extension) ─────────────
const LANG_FN = {
  python:     () => langs.python(),
  javascript: () => langs.javascript({ jsx: false }),
  typescript: () => langs.javascript({ typescript: true }),
  jsx:        () => langs.javascript({ jsx: true }),
  tsx:        () => langs.javascript({ jsx: true, typescript: true }),
  html:       () => langs.html(),
  css:        () => langs.css(),
  json:       () => langs.json(),
  java:       () => langs.java(),
  cpp:        () => langs.cpp(),
  c:          () => langs.c(),
  go:         () => langs.go(),
  rust:       () => langs.rust(),
  php:        () => langs.php(),
  ruby:       () => langs.ruby(),
  perl:       () => langs.perl(),
  lua:        () => langs.lua(),
  sql:        () => langs.sql(),
  mysql:      () => langs.sql(),
  pgsql:      () => langs.sql(),
  shell:      () => langs.shell(),
  bash:       () => langs.shell(),
  markdown:   () => langs.markdown(),
  xml:        () => langs.xml(),
  yaml:       () => langs.yaml(),
};

// ── Editor theme factory: build EditorView.theme from central THEMES ─────────
function buildEditorTheme(themeId) {
  const t = getTheme(themeId);
  const c = t.codemirror;
  return EditorView.theme(
    {
      "&": {
        backgroundColor: t.background,
        color: c.fg,
        height: "100%",
        lineHeight: "1.35",
        fontFamily: "JetBrains Mono, 'Fira Code', 'SF Mono', Menlo, Consolas, monospace",
      },
      ".cm-content": {
        caretColor: c.caret,
        padding: "2px 0 60px 0",
        minHeight: "100%",
      },
      ".cm-focused": { outline: "none" },
      ".cm-cursor, .cm-dropCursor": {
        borderLeftColor: c.caret,
        borderLeftWidth: "2px",
      },
      ".cm-selectionBackground": { backgroundColor: c.selection + " !important" },
      "&.cm-focused .cm-selectionBackground": { backgroundColor: c.selection + " !important" },
      "::selection": { backgroundColor: c.selection },
      ".cm-activeLine": { backgroundColor: c.activeLineBg },
      ".cm-gutters": {
        backgroundColor: t.background,
        color: c.gutterFg,
        border: "none",
        borderRight: "1px solid " + (t.panelBackground || "#1e2530"),
        userSelect: "none",
      },
      ".cm-lineNumbers": { minWidth: "36px" },
      ".cm-lineNumbers .cm-gutterElement": {
        textAlign: "right",
        paddingRight: "8px",
        paddingLeft: "4px",
        fontSize: "0.8em",
        lineHeight: "1.35",
      },
      ".cm-activeLineGutter": { backgroundColor: c.activeLineBg, color: c.activeGutterFg + " !important" },
      ".cm-matchingBracket": {
        outline: "1px solid " + c.bracket + "66",
        borderRadius: "1px",
        backgroundColor: c.bracket + "22",
      },
      ".cm-scroller": { overflow: "auto", fontFamily: "inherit" },
      ".cm-line": { paddingLeft: "6px", paddingRight: "4px", lineHeight: "1.35" },
      // Search panel styling
      ".cm-panels": { backgroundColor: t.panelBackground || "#161b22", color: c.fg },
      ".cm-panels-bottom": { borderTop: "1px solid #1e2530" },
      ".cm-searchMatch": { backgroundColor: c.caret + "44", outline: "1px solid " + c.caret + "88" },
      ".cm-searchMatch-selected": { backgroundColor: c.caret + "88" },
      ".cm-textfield": {
        backgroundColor: t.background, color: c.fg,
        border: "1px solid " + (t.panelBackground || "#1e2530"), borderRadius: "4px",
        padding: "2px 6px",
      },
      ".cm-button": {
        backgroundColor: t.background, color: c.fg,
        border: "1px solid " + (t.panelBackground || "#1e2530"), borderRadius: "4px",
        padding: "2px 8px",
      },
    },
    { dark: true }
  );
}

// ── Syntax highlighting factory ───────────────────────────────────────────────
function buildHighlight(themeId) {
  const c = getTheme(themeId).codemirror;
  return HighlightStyle.define([
    { tag: [t.comment, t.lineComment, t.blockComment], color: c.comment, fontStyle: "italic" },
    { tag: [t.keyword, t.controlKeyword, t.operatorKeyword, t.definitionKeyword], color: c.keyword, fontWeight: "bold" },
    { tag: t.moduleKeyword, color: c.keyword },
    { tag: [t.string, t.regexp], color: c.string },
    { tag: t.escape, color: c.bracket },
    { tag: [t.number, t.integer, t.float], color: c.number },
    { tag: [t.bool, t.null, t.self], color: c.number },
    { tag: [t.typeName, t.className, t.namespace], color: c.type },
    { tag: [t.function(t.variableName), t.function(t.propertyName)], color: c.func },
    { tag: t.definition(t.variableName), color: c.fg },
    { tag: [t.operator, t.arithmeticOperator, t.logicOperator, t.bitwiseOperator, t.compareOperator, t.updateOperator, t.punctuation], color: c.operator },
    { tag: [t.bracket, t.paren], color: c.bracket },
    { tag: t.squareBracket, color: c.number },
    { tag: [t.propertyName, t.variableName], color: c.fg },
    { tag: t.attributeName, color: c.func },
    { tag: [t.tagName, t.angleBracket], color: c.tag },
    { tag: t.separator, color: c.operator },
    { tag: t.invalid, color: c.invalid, textDecoration: "underline" },
    { tag: t.meta, color: c.comment },
  ]);
}

// ── Symbol bar (shown at bottom — sits above keyboard when it opens) ──────────
// Minimal set matching Acode style + useful extras
const BOTTOM_SYMBOLS = [
  "Tab", "|", ":", ";", ".", ",",
  "(", ")", "{", "}", "[", "]",
  '"', "'", "`",
  "=", "+", "-", "*", "/", "%",
  "<", ">", "!", "&", "|",
  "#", "@", "_", "\\",
  "->", "=>",
];

export default function MobileEditorPane({
  path,
  value,
  monacoLanguage,
  onChange,
  onRequestRun,
  isTablet = false,
  themeId = "brand",
}) {
  const viewRef = useRef(null);
  const onRequestRunRef = useRef(onRequestRun);
  useEffect(() => { onRequestRunRef.current = onRequestRun; }, [onRequestRun]);

  const [fontSize, setFontSize] = useState(isTablet ? 13 : 13);
  const [copied, setCopied] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Language extension
  const langExt = useMemo(() => {
    const fn = LANG_FN[monacoLanguage];
    if (!fn) return [];
    try { return [fn()]; } catch (_) { return []; }
  }, [monacoLanguage]);

  // Font-size theme
  const fontTheme = useMemo(
    () => EditorView.theme({ "&": { fontSize: `${fontSize}px` } }),
    [fontSize]
  );

  // Build theme extensions whenever themeId changes
  const editorThemeExt = useMemo(() => buildEditorTheme(themeId), [themeId]);
  const highlightExt = useMemo(() => buildHighlight(themeId), [themeId]);

  const extensions = useMemo(
    () => [
      editorThemeExt, fontTheme, syntaxHighlighting(highlightExt),
      search({ top: true }),
      keymap.of(searchKeymap),
      ...langExt,
    ],
    [langExt, fontTheme, editorThemeExt, highlightExt]
  );

  // ── Editor actions ────────────────────────────────────────────────────────
  const getView = () => viewRef.current;

  function handleUndo()   { const v = getView(); if (v) { undo(v);        v.focus(); } }
  function handleRedo()   { const v = getView(); if (v) { redo(v);        v.focus(); } }
  function handleIndent() { const v = getView(); if (v) { indentMore(v);  v.focus(); } }
  function handleOutdent(){ const v = getView(); if (v) { indentLess(v);  v.focus(); } }

  async function handleCopy() {
    const v = getView();
    if (!v) return;
    const sel = v.state.selection.main;
    const text = sel.empty
      ? v.state.doc.toString()                        // copy all if no selection
      : v.state.sliceDoc(sel.from, sel.to);
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); }
    catch (_) {}
  }

  async function handlePaste() {
    const v = getView();
    if (!v) return;
    try {
      const text = await navigator.clipboard.readText();
      if (text) { v.dispatch(v.state.replaceSelection(text)); v.focus(); }
    } catch (_) {}
  }

  // Insert symbol at cursor (called from bottom bar)
  function insertSymbol(sym) {
    const v = getView();
    if (!v) return;
    if (sym === "Tab") {
      indentMore(v);
    } else {
      v.dispatch(v.state.replaceSelection(sym));
    }
    v.focus();
  }

  function toggleSearch() {
    const v = getView();
    if (!v) return;
    if (searchOpen) {
      closeSearchPanel(v);
      setSearchOpen(false);
    } else {
      openSearchPanel(v);
      setSearchOpen(true);
    }
  }

  const themeBg = getTheme(themeId).background;

  // ── Toolbar button component ─────────────────────────────────────────────
  const ToolBtn = ({ onClick, title, children, active, ...rest }) => (
    <button
      onClick={onClick}
      title={title}
      {...rest}
      className={
        "flex-none flex items-center justify-center min-w-[32px] h-7 px-1.5 rounded transition-all duration-100 active:scale-90 select-none " +
        (active
          ? "bg-ide-accent/20 text-ide-accent border border-ide-accent/40"
          : "bg-ide-surface/70 hover:bg-ide-borderHi text-ide-mute hover:text-ide-text")
      }
    >
      {children}
    </button>
  );

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: themeBg }}>

      {/* ── Compact Top Action Toolbar ── */}
      <div className="flex-none border-b border-[#1e2530]" style={{ backgroundColor: themeBg }}>
        <div className="flex items-center gap-1 px-2 py-1 overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <ToolBtn onClick={handleUndo}    title="Undo"><Undo2 className="h-3.5 w-3.5" /></ToolBtn>
          <ToolBtn onClick={handleRedo}    title="Redo"><Redo2 className="h-3.5 w-3.5" /></ToolBtn>
          <ToolBtn onClick={handleIndent}  title="Indent"><AlignJustify className="h-3.5 w-3.5" /></ToolBtn>
          <ToolBtn onClick={handleOutdent} title="Dedent"><AlignLeft className="h-3.5 w-3.5" /></ToolBtn>
          <div className="h-3.5 w-px bg-[#1e2530] mx-0.5 flex-none" />
          <ToolBtn onClick={toggleSearch} title="Find / Replace" active={searchOpen} data-testid="mobile-find-btn">
            <Search className="h-3.5 w-3.5" />
            <span className="ml-1 text-[10px] font-mono">Find</span>
          </ToolBtn>
          <ToolBtn onClick={handleCopy} title={copied ? "Copied!" : "Copy"} active={copied}>
            <Copy className="h-3.5 w-3.5" />
            <span className="ml-1 text-[10px] font-mono">{copied ? "\u2713" : "Copy"}</span>
          </ToolBtn>
          <ToolBtn onClick={handlePaste} title="Paste">
            <ClipboardPaste className="h-3.5 w-3.5" />
            <span className="ml-1 text-[10px] font-mono">Paste</span>
          </ToolBtn>
          <div className="h-3.5 w-px bg-[#1e2530] mx-0.5 flex-none" />
          <ToolBtn onClick={() => setFontSize((s) => Math.max(10, s - 1))} title="Smaller">
            <ZoomOut className="h-3 w-3" />
          </ToolBtn>
          <span className="text-[10px] text-[#3d4857] font-mono px-0.5 flex-none">{fontSize}</span>
          <ToolBtn onClick={() => setFontSize((s) => Math.min(20, s + 1))} title="Larger">
            <ZoomIn className="h-3 w-3" />
          </ToolBtn>
        </div>
      </div>

      {/* ── CodeMirror 6 Editor ── */}
      <div className="flex-1 min-h-0 overflow-hidden" style={{ backgroundColor: themeBg }}>
        <CodeMirror
          value={value}
          height="100%"
          style={{ height: "100%", overflow: "auto" }}
          basicSetup={{
            lineNumbers:               true,
            highlightActiveLineGutter: true,
            highlightActiveLine:       true,
            foldGutter:                false,
            drawSelection:             true,
            dropCursor:                true,
            allowMultipleSelections:   true,
            indentOnInput:             true,
            bracketMatching:           true,
            closeBrackets:             true,
            autocompletion:            isTablet,
            completionKeymap:          isTablet,
            rectangularSelection:      false,
            crosshairCursor:           false,
            highlightSelectionMatches: false,
            searchKeymap:              false,
            history:                   true,
            tabSize:                   2,
          }}
          extensions={extensions}
          onChange={onChange}
          onCreateEditor={(view) => { viewRef.current = view; }}
        />
      </div>

      {/* ── Bottom Symbol Bar (floats above virtual keyboard) ── */}
      <div
        className="flex-none border-t border-[#1e2530] overflow-x-auto"
        style={{ backgroundColor: "#161b22", WebkitOverflowScrolling: "touch" }}
        data-testid="symbol-bar"
      >
        <div className="flex items-center min-w-max px-1 py-1 gap-0.5">
          {BOTTOM_SYMBOLS.map((sym, i) => (
            <button
              key={`${sym}-${i}`}
              onPointerDown={(e) => { e.preventDefault(); insertSymbol(sym); }}
              data-testid={`sym-${sym.replace(/[^a-zA-Z0-9]/g, "_")}`}
              className={
                "flex-none h-7 min-w-[34px] px-1.5 rounded-sm text-[12px] font-mono select-none transition-all active:scale-90 " +
                (sym === "Tab"
                  ? "bg-[#1e2b3a] text-[#7c3aed] border border-[#7c3aed]/30 font-semibold text-[11px]"
                  : sym === "|"
                  ? "text-[#1e2530] cursor-default pointer-events-none w-px !min-w-0 !px-0 mx-0.5"
                  : "bg-[#1a2130] text-[#c9d1d9] border border-[#1e2530] hover:bg-[#1e2b3a] active:bg-[#7c3aed]/20")
              }
            >
              {sym}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
